import { ApolloError } from '@apollo/client'
import { MAX_WIDTH_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import usePoolAndPairQuery from 'graphql/competition/PoolAndPair'
import { PAGE_SIZE } from 'graphql/data/TopTokens'
import React, { ReactNode } from 'react'
import { AlertTriangle } from 'react-feather'
import styled from 'styled-components/macro'

import { PoolAndPairQuery } from '../../../graphql/competition/__generated__/types-and-hooks'
import { LoadingPoolAndPairRow, PoolAndPairHeaderRow, PoolAndPairRow } from './Row'
import { TrackedTokenRow } from './TokenRow'

const Wrapper = styled.div`
  gap: 1rem;
  display: flex;
  flex-wrap: wrap;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  justify-content: space-between;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
  }
`

const GridContainer = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: 12px;
  align-items: center;
  background: ${({ theme }) => theme.backgroundSwap};
  border: 1px solid ${({ theme }) => theme.backgroundSwapOutline};
  box-shadow: ${({ theme }) => theme.backgroundSwapShadow};
`

const TokenDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  width: 100%;
`

const NoTokenDisplay = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 60px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 16px;
  font-weight: 500;
  align-items: center;
  padding: 0px 28px;
  gap: 8px;
`

enum ICategory {
  POOL = 'Pool V3',
  PAIR = 'Pair V2',
  TOKEN = 'Token',
}

function NoTokensState({ message, category, id }: { message: ReactNode; category: ICategory; id: string }) {
  return (
    <GridContainer id={id}>
      {/* @ts-ignore */}
      <PoolAndPairHeaderRow category={category} />
      <NoTokenDisplay>{message}</NoTokenDisplay>
    </GridContainer>
  )
}

const LoadingRows = ({ rowCount }: { rowCount: number }) => (
  <>
    {Array(rowCount)
      .fill(null)
      .map((_, index) => {
        return <LoadingPoolAndPairRow key={index} first={index === 0} last={index === rowCount - 1} />
      })}
  </>
)

function LoadingTokenTable({
  rowCount = PAGE_SIZE,
  category,
  id,
}: {
  rowCount?: number
  category: ICategory
  id: string
}) {
  return (
    <GridContainer id={id}>
      {/* @ts-ignore */}
      <PoolAndPairHeaderRow category={category} />
      <TokenDataContainer>
        <LoadingRows rowCount={rowCount} />
      </TokenDataContainer>
    </GridContainer>
  )
}

export interface IPoolOrPair {
  id: string
  token0: {
    id: string
    symbol: string
  }
  token1: {
    id: string
    symbol: string
  }
  feeTier?: number
}

const Table = ({
  isLoading,
  data,
  error,
  category,
  id,
}: {
  error?: ApolloError
  isLoading: boolean
  data: any
  category: ICategory
  id: string
}) => {
  if (isLoading) {
    return <LoadingTokenTable rowCount={PAGE_SIZE} category={category} id={id || ''} />
  } else if (error) {
    return (
      <NoTokensState
        id={id}
        message={
          <>
            <AlertTriangle size={16} />
            An error occurred loading your trades. Please try again.
            <p>{error.message}</p>
          </>
        }
        category={category}
      />
    )
  } else if (data.length === 0) {
    return <NoTokensState id={id} message={<>No data</>} category={category} />
  } else {
    return (
      <GridContainer id={id}>
        {/* @ts-ignore */}
        <PoolAndPairHeaderRow category={category} />
        <TokenDataContainer>
          {category === ICategory.TOKEN
            ? data.map(
                (item: PoolAndPairQuery['tokens'][number], index: number) =>
                  item?.id && (
                    <TrackedTokenRow
                      key={item.id}
                      tokenListIndex={index}
                      tokenListLength={data.length}
                      data={item}
                      sortRank={index + 1}
                    />
                  )
              )
            : data.map(
                (item: IPoolOrPair, index: number) =>
                  item?.id && (
                    <PoolAndPairRow
                      key={item.id}
                      tokenListIndex={index}
                      tokenListLength={data.length}
                      data={item}
                      // dayTrade={userTrades.data.dayTrade[item.token.id]}
                      // totalTrade={item}
                      // sparklineMap={sparklines}
                      //@ts-ignore
                      sortRank={index + 1}
                    />
                  )
              )}
        </TokenDataContainer>
      </GridContainer>
    )
  }
}

const PoolAndPairTable = () => {
  const { isLoading, data, error } = usePoolAndPairQuery()

  return (
    <Wrapper>
      <Table
        id="token-table"
        category={ICategory.TOKEN}
        data={data && data.tokens ? data.tokens : []}
        error={error}
        isLoading={isLoading}
      />
      <Table
        id="pool-table"
        category={ICategory.POOL}
        data={data && data.pools ? data.pools : []}
        error={error}
        isLoading={isLoading}
      />
      <Table
        id="pair-table"
        category={ICategory.PAIR}
        data={data && data.pairs ? data.pairs : []}
        error={error}
        isLoading={isLoading}
      />
    </Wrapper>
  )
}

export default PoolAndPairTable
